// src/contexts/ThemeContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Theme management.
 *
 * Resolution order on first load:
 *   1. localStorage["theme"]               — user's last explicit choice
 *   2. REACT_APP_DEFAULT_THEME env var     — site-wide default
 *   3. "light"                             — hard fallback
 *
 * The theme is applied as a `data-theme` attribute on <html>, which the
 * CSS tokens read via `[data-theme="light"]` / `[data-theme="dark"]`
 * selectors.
 *
 * For zero-flash-on-load, you can optionally add this inline script
 * just before </head> in public/index.html — it runs before React mounts
 * and avoids the brief light-flash when the saved theme is dark:
 *
 *   <script>
 *     try {
 *       var t = localStorage.getItem('theme');
 *       if (t !== 'light' && t !== 'dark') {
 *         t = '%REACT_APP_DEFAULT_THEME%';
 *         if (t !== 'light' && t !== 'dark') t = 'light';
 *       }
 *       document.documentElement.setAttribute('data-theme', t);
 *     } catch(e) {}
 *   </script>
 *
 * Without that script, there's still no flash for the *default* theme —
 * only a brief flash on every load if the user has manually chosen the
 * non-default theme. Most apps live with that.
 */

const STORAGE_KEY = "theme";
const ATTR = "data-theme";
const VALID = new Set(["light", "dark"]);

const sanitize = (value) => (VALID.has(value) ? value : null);

const resolveInitial = () => {
  // Pull saved choice first.
  if (typeof window !== "undefined") {
    try {
      const saved = sanitize(window.localStorage.getItem(STORAGE_KEY));
      if (saved) return saved;
    } catch {
      /* localStorage may be disabled (private mode, embedded iframe) — ignore */
    }
  }
  // Then env default.
  const envDefault = sanitize(String(process.env.REACT_APP_DEFAULT_THEME || "").trim().toLowerCase());
  if (envDefault) return envDefault;
  // Hard fallback.
  return "light";
};

const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  // Lazy initializer so first render already reflects the chosen theme.
  const [theme, setThemeState] = useState(resolveInitial);

  // Apply to <html> on every change; also handle the very first mount in
  // case there's no inline pre-mount script.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute(ATTR, theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    const safe = sanitize(next);
    if (!safe) return;
    setThemeState(safe);
    try {
      window.localStorage.setItem(STORAGE_KEY, safe);
    } catch {
      /* ignore — non-critical */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
